<template>
    <div class="panel panel-default panel-dashboard">
        <div class="panel-heading">
            <h5>
                Renewal Percentage
            </h5>
        </div>

        <div v-if="!loading" class="panel-body">
            <canvas width="400" height="250"></canvas>
        </div>

        <renewal-percentage-breakdown-modal
            v-if="monthBreakdown"
            :filters="filters"
            :month="monthBreakdown"
        />
    </div>
</template>

<script>
import http from '@/http'
import moment from 'moment'
import RenewalPercentageBreakdownModal from '@/components/Modals/Dashboard/Subscriptions/RenewalPercentageBreakdown'

export default {
    components: {RenewalPercentageBreakdownModal},

    props: ['year', 'portal', 'user', 'subscriberType'],

    data() {
        return {
            renewedRows: null,
            endingRows: null,
            chart: null,

            monthBreakdown: null,

            loading: true
        }
    },

    mounted() {
        this.load()
    },

    watch: {
        year() {
            this.load()
        },

        portal() {
            this.load()
        },

        user() {
            this.load()
        },

        subscriberType() {
            this.load()
        },

        loading(value) {
            if (!value) {
                this.$nextTick(this.draw)
            }
        }
    },

    computed: {
        filters() {
            let filters = []

            if (this.portal && this.portal.length) {
                filters.push({dimension: 'portal_id', operator: 'in', value: this.portal})
            }

            if (this.user) {
                filters.push({dimension: 'user_id', value: this.user})
            }

            if (this.subscriberType) {
                filters.push({dimension: 'subscriber_type', value: this.subscriberType})
            }

            return filters
        },

        months() {
            let startDate = moment().year(this.year).subtract(1, 'years').startOf('year').add(1, 'months')

            let endDate = moment().year(this.year).endOf('year')

            let result = []

            while (startDate.isBefore(endDate)) {
                result.push(startDate.clone())

                startDate.add(1, 'month')
            }

            return result
        },

        rows() {
            return this.months.map(month => {
                return {
                    month: month,
                    renewed: this.getRenewedNumber(month),
                    ending: this.getEndingNumber(month)
                }
            })
        },

        accumulatedRows() {
            return [...Array(12).keys()]
                .map(index => {
                    return {
                        month: this.months[index + 11],
                        renewed: this.rows.slice(index, index + 12).reduce((sum, row) => sum + row.renewed, 0),
                        ending: this.rows.slice(index, index + 12).reduce((sum, row) => sum + row.ending, 0)
                    }
                })
        }
    },

    methods: {
        load() {
            this.loading = true

            Promise.all(this.getPromises())
                .then(responses => {
                    this.renewedRows = responses[0].data.data

                    this.endingRows = responses[1].data.data

                    this.loading = false
                })
        },

        getPromises() {
            return [
                http.post('/api/v1/reports/dashboard', {
                    start: moment().year(this.year).subtract(1, 'years').startOf('year').add(1, 'months').format('YYYY-MM-DD'),
                    end: moment().year(this.year).endOf('year').format('YYYY-MM-DD'),
                    metrics: ['ending_subscription_count'],
                    dimensions: ['month'],
                    filters: this.filters.concat([{dimension: 'is_last_subscription_period', value: false}])
                }),
                http.post('/api/v1/reports/dashboard', {
                    start: moment().year(this.year).subtract(1, 'years').startOf('year').add(1, 'months').format('YYYY-MM-DD'),
                    end: moment().year(this.year).endOf('year').format('YYYY-MM-DD'),
                    metrics: ['ending_subscription_count'],
                    dimensions: ['month'],
                    filters: this.filters
                })
            ]
        },

        getRenewedNumber(month) {
            let row = this.renewedRows.find(row => row.month === month.format('YYYY-MM'))

            return row ? row.ending_subscription_count : 0
        },

        getEndingNumber(month) {
            let row = this.endingRows.find(row => row.month === month.format('YYYY-MM'))

            return row ? row.ending_subscription_count : 0
        },

        draw() {
            this.chart = new Chart(this.$el.querySelector('canvas').getContext('2d'), {
                type: 'bar',

                data: {
                    labels: this.months.slice(11, 24).map(month => month.format('YYYY-MM')),
                    datasets: [
                        {
                            label: 'Renewal Percentage',
                            data: this.rows.slice(11, 24).map(row => Math.round(row.renewed / row.ending * 100)),
                            backgroundColor: '#a4cf30'
                        },
                        {
                            label: 'Accumulated Renewal Percentage (12 months)',
                            data: this.accumulatedRows.map(row => Math.round(row.renewed / row.ending * 100)),
                            type: 'line',
                            fill: false
                        }
                    ]
                },

                options: {
                    scales: {
                        xAxes: [{
                            stacked: true
                        }],
                        yAxes: [{
                            stacked: true,
                            ticks: {
                                beginAtZero: true,
                                callback: value => value + ' %'
                            }
                        }]
                    },

                    legend: {
                        position: 'bottom'
                    },

                    layout: {
                        padding: {
                            top: 25
                        }
                    },

                    tooltips: {
                        callbacks: {
                            label: (t, d) => d.datasets[t.datasetIndex].label + ': ' + d.datasets[t.datasetIndex].data[t.index] + ' %',
                            title: (t, d) => {
                                if (t[0].datasetIndex === 0) {
                                    return this.months[11 + t[0].index].format('YYYY-MM')
                                }

                                return this.months[t[0].index].format('YYYY-MM') + ' - ' + this.months[11 + t[0].index].format('YYYY-MM')
                            }
                        }
                    },

                    plugins: {
                        datalabels: {
                            anchor: 'end',
                            align: 'end',
                            formatter: (value, context) => value + ' %'
                        }
                    },

                    onClick: (event, elements) => {
                        if (elements.length) {
                            this.monthBreakdown = this.months.slice(11, 24)[elements[0]['_index']].format('YYYY-MM-DD')

                            this.$nextTick(() => $('#show-renewal-percentage-breakdown-modal').modal('show'))
                        }
                    }
                }
            })
        }
    }
}
</script>
